import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import brokenImage from '../../../../images/icon/image-broken.svg';
import type { LexicalEditor, NodeKey, NodeSelection, RangeSelection, } from 'lexical';
import {
	$getNodeByKey,
	$getSelection,
	$isNodeSelection,
	CLICK_COMMAND,
	COMMAND_PRIORITY_LOW,
	DRAGSTART_COMMAND,
	KEY_BACKSPACE_COMMAND,
	KEY_DELETE_COMMAND,
	KEY_ENTER_COMMAND,
	SELECTION_CHANGE_COMMAND,
} from 'lexical';
import * as React from 'react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import DialogStyled from "../../../components/dialogStyled";
import InlineImageForm from "../../nodes/InlineImageForm";
import { $isInlineImageNode, InlineImageNode, type InlineImagePayload, type Position } from '../../nodes/InlineImageNode';

const imageCache = new Set();

function useSuspenseImage(src: string) {
	if (!imageCache.has(src)) {
		throw new Promise((resolve) => {
			const img = new Image();
			img.src = src;
			img.onload = () => {
				imageCache.add(src);
				resolve(null);
			};
			img.onerror = () => {
				imageCache.add(src);
			};
		});
	}
}

function LazyImage({
	altText,
	className,
	imageRef,
	src,
	width,
	height,
	position,
	onError,
}: {
	altText: string;
	className: string|null;
	height: 'inherit'|number;
	imageRef: {current: null|HTMLImageElement};
	src: string;
	width: 'inherit'|number;
	position: Position;
	onError: () => void;
}): JSX.Element {
	useSuspenseImage(src);
	return (
		<img
			className={className || undefined}
			src={src}
			alt={altText}
			ref={imageRef}
			data-position={position}
			width={width}
			height={height}
			style={{display: 'block'}}
			onError={onError}
			draggable="false"
		/>
	);
}

export function UpdateInlineImageDialog({activeEditor, nodeKey, onClose}: {
	activeEditor: LexicalEditor;
	nodeKey: NodeKey;
	onClose: () => void;
}): JSX.Element {
	const editorState = activeEditor.getEditorState();
	const node = editorState.read(() => $getNodeByKey(nodeKey) as InlineImageNode);

	const [width, setWidth] = useState(node.getWidth());
	const [height, setHeight] = useState(node.getHeight());
	const [src, setSrc] = useState(node.getSrc());
	const [classes, setClasses] = useState(node.getClasses());
	const [altText, setAltText] = useState(node.getAltText());
	const [position, setPosition] = useState<Position>(node.getPosition());

	//console.log(node.getSrc());

	const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPosition(e.target.value as Position);
	};

	const handleOnConfirm = () => {
		const payload: InlineImagePayload = {altText, position, src, classes};

		if (width !== 'inherit' && width !== 0) {
			payload.width = width;
		}
		if (height !== 'inherit' && height !== 0) {
			payload.height = height;
		}

		//console.log(payload);
		if (node) {
			activeEditor.update(() => {
				node.update(payload);
			});
		}
		onClose();
	};

	return <>
		<InlineImageForm
			src={src}
			setSrc={setSrc}
			altText={altText} setAltText={setAltText}
			width={(width !== 'inherit' && width !== 0) ? width : 0} setWidth={setWidth}
			height={(height !== 'inherit' && height !== 0) ? height : 0} setHeight={setHeight}
			position={position} handlePositionChange={handlePositionChange}
			classes={classes} setClasses={setClasses}
			handleOnClick={handleOnConfirm}
			isDisabled={false}
		/>
	</>
}

function BrokenImage(): JSX.Element {
	return (
		<img
			src={brokenImage}
			style={{
				height: 200,
				opacity: 0.2,
				width: 200,
			}}
			draggable="false"
			alt=""
		/>
	);
}

export default function InlineImageComponent({
	src,
	width,
	height,
	altText,
	position,
	classes,
	nodeKey,
}: {
	src: string;
	width: 'inherit'|number;
	height: 'inherit'|number;
	altText: string;
	position: Position;
	classes: string
	nodeKey: NodeKey;
}): JSX.Element {
	const imageRef = useRef<null|HTMLImageElement>(null);
	const buttonRef = useRef<HTMLButtonElement|null>(null);
	const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
	const [editor] = useLexicalComposerContext();
	const [selection, setSelection] = useState<RangeSelection|NodeSelection|null>(null);
	const activeEditorRef = useRef<LexicalEditor|null>(null);
	const [isLoadError, setIsLoadError] = useState<boolean>(false);
	const [isModalImageOpen, setIsModalImageOpen] = useState(false);

	const onDelete = useCallback(
		(event: KeyboardEvent) => {
			if (isSelected && $isNodeSelection($getSelection())) {
				event.preventDefault();
				const node = $getNodeByKey(nodeKey);
				if ($isInlineImageNode(node)) {
					node.remove();
				}
			}
			return false;
		},
		[isSelected, nodeKey],
	);

	const onEnter = useCallback(
		(event: KeyboardEvent) => {
			const latestSelection = $getSelection();
			const buttonElem = buttonRef.current;
			if (
				isSelected &&
				$isNodeSelection(latestSelection) &&
				latestSelection.getNodes().length === 1
			) {
				if (
					buttonElem !== null &&
					buttonElem !== document.activeElement
				) {
					event.preventDefault();
					buttonElem.focus();
					return true;
				}
			}
			return false;
		},
		[isSelected],
	);

	useEffect(() => {
		let isMounted = true;
		const unregister = mergeRegister(
			editor.registerUpdateListener(({editorState}) => {
				if (isMounted) {
					setSelection(editorState.read(() => $getSelection()));
				}
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				(_, activeEditor) => {
					activeEditorRef.current = activeEditor;
					return false;
				},
				COMMAND_PRIORITY_LOW,
			),
			editor.registerCommand<MouseEvent>(
				CLICK_COMMAND,
				(payload) => {
					const event = payload;
					if (event.target === imageRef.current) {
						if (event.shiftKey) {
							setSelected(!isSelected);
						} else {
							clearSelection();
							setSelected(true);
						}
						return true;
					}

					return false;
				},
				COMMAND_PRIORITY_LOW,
			),
			editor.registerCommand(
				DRAGSTART_COMMAND,
				(event) => {
					if (event.target === imageRef.current) {
						// TODO This is just a temporary workaround for FF to behave like other browsers.
						// Ideally, this handles drag & drop too (and all browsers).
						event.preventDefault();
						return true;
					}
					return false;
				},
				COMMAND_PRIORITY_LOW,
			),
			editor.registerCommand(
				KEY_DELETE_COMMAND,
				onDelete,
				COMMAND_PRIORITY_LOW,
			),
			editor.registerCommand(
				KEY_BACKSPACE_COMMAND,
				onDelete,
				COMMAND_PRIORITY_LOW,
			),
			editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
		);
		return () => {
			isMounted = false;
			unregister();
		};
	}, [
		clearSelection,
		editor,
		isSelected,
		nodeKey,
		onDelete,
		onEnter,
		setSelected,
	]);

	const draggable = isSelected && $isNodeSelection(selection);
	const isFocused = isSelected;
	return (
		<Suspense fallback={null}>
			<div draggable={draggable}>
				<button className="image-edit-button" ref={buttonRef} onClick={() => setIsModalImageOpen(true)}>
					Edit
				</button>
				{isLoadError ? (
					<BrokenImage />
				) : (
					<LazyImage
						className={
							isFocused
								? `focused ${$isNodeSelection(selection) ? 'draggable' : ''}`
								: null
						}
						src={src}
						altText={altText}
						imageRef={imageRef}
						width={width}
						height={height}
						position={position}
						onError={() => setIsLoadError(true)}
					/>
				)}
			</div>
			<DialogStyled
				isOpen={isModalImageOpen}
				onClose={() => setIsModalImageOpen(false)}
				classesSize="max-w-[90vw] max-h-[97vh]"
			>
				<UpdateInlineImageDialog
					activeEditor={editor}
					nodeKey={nodeKey}
					onClose={() => setIsModalImageOpen(false)}
				/>
			</DialogStyled>
		</Suspense>
	);
}
